// Haptics. Android: the Vibration API, driven by game events (kerbs, impacts).
// iOS Safari has no Vibration API. The only route to the Taptic Engine is the
// native <input type="checkbox" switch> control, which ticks when toggled, and
// since iOS 26.5 only a direct finger tap on the switch fires it (script-driven
// toggles no longer do). So on iOS, haptics are limited to UI taps: we overlay
// an invisible switch on buttons such as Start and Re-centre.

const canVibrate = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

let enabled = true;

export function setHapticsEnabled(on: boolean): void {
  enabled = on;
  // Hide the tap switches so buttons are tapped directly (no tick).
  if (typeof document !== 'undefined') document.documentElement.classList.toggle('no-haptics', !on);
}

export function hapticsEnabled(): boolean {
  return enabled;
}

/** Game-event vibration (no-op where unsupported, e.g. iOS). */
export function vibrate(pattern: number | number[]): void {
  if (!enabled || !canVibrate) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Some browsers throw if called before user activation.
  }
}

/**
 * Make a button give a Taptic tick on iOS when tapped. Adds an invisible native
 * switch covering the button; the tap lands on the switch (firing the haptic)
 * and is forwarded as a click to the button. Harmless elsewhere.
 */
export function addTapHaptic(button: HTMLElement): void {
  const sw = document.createElement('input');
  sw.type = 'checkbox';
  sw.setAttribute('switch', '');
  sw.className = 'haptic-switch';
  sw.tabIndex = -1;
  sw.setAttribute('aria-hidden', 'true');
  sw.addEventListener('click', (e) => {
    e.stopPropagation();
    button.click();
  });
  sw.addEventListener('pointerdown', (e) => e.stopPropagation());
  if (getComputedStyle(button).position === 'static') button.style.position = 'relative';
  button.appendChild(sw);
}

/** Rate-limited rumble while driving over kerbs (Android). */
export class KerbRumble {
  private lastPulse = 0;

  update(onKerb: boolean, speed: number): void {
    if (!onKerb || speed < 8) return;
    const now = performance.now();
    // Faster = more frequent pulses, like driving over kerb ridges.
    const interval = Math.max(60, 400 - speed * 4);
    if (now - this.lastPulse > interval) {
      this.lastPulse = now;
      vibrate(18);
    }
  }
}
